import { useEffect, useRef } from "react";
import { useActivities } from "../context/ActivitiesContext";
import { useProducts } from "../context/ProductContext";
import { useStorageItems } from "../context/StorageItemsContext";

type UseEnsureCatalogDataOptions = {
    enabled?: boolean;
    productSource?: "allProducts" | "products";
    reloadOnMount?: boolean;
};

const useEnsureCatalogData = ({
    enabled = true,
    productSource = "allProducts",
    reloadOnMount = false,
}: UseEnsureCatalogDataOptions = {}) => {
    const { products, allProducts, newProducts, packages, loading, reloadProducts } = useProducts();
    const { items, loading: loadingItems, reloadItems } = useStorageItems();
    const { activities, loading: loadingActivities, reloadActivities } = useActivities();
    const didReloadOnMount = useRef(false);

    useEffect(() => {
        if (!enabled) return;
        if (reloadOnMount && !didReloadOnMount.current) {
            didReloadOnMount.current = true;
            reloadProducts();
            reloadItems();
            reloadActivities();
            return;
        }

        const productData = productSource === "products" ? products : allProducts;

        if (!loading && !productData) {
            reloadProducts();
        }
        if (!loadingItems && !items) {
            reloadItems();
        }
        if (!loadingActivities && !activities) {
            reloadActivities();
        }
    }, [
        enabled,
        reloadOnMount,
        productSource,
        loading,
        loadingItems,
        loadingActivities,
        products,
        allProducts,
        items,
        activities,
        reloadProducts,
        reloadItems,
        reloadActivities,
    ]);

    return {
        products,
        allProducts,
        newProducts,
        packages,
        items,
        activities,
        loading,
        loadingItems,
        loadingActivities,
        reloadProducts,
        reloadItems,
        reloadActivities,
    };
};

export default useEnsureCatalogData;
